import { useEffect, useRef, useState } from 'react'
import {
  AdminsApi,
  AdminsEcommerceGetProductCategoryItem,
  AdminsEcommerceInventoryStockLocationListResponseItem,
  Configuration,
  VoucherDiscountTypeEnum,
  VoucherTypeEnum,
} from 'generated/api'
import { axiosDefaultConfig } from 'constants/axios'
import FormVoucherValue from 'constants/interfaceFormik/loyaltyVoucher'
import { useNavigate } from 'react-router-dom'
import Toast from 'components/toast/Toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

export enum VoucherStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const useVoucherForm = () => {
  const navigate = useNavigate()
  const { toast, toastContainer } = Toast()
  const [voucherForm, setVoucherForm] = useState<FormVoucherValue>({
    imageUrl: '',
    isAllProduct: true,
    isSawitPoin: false,
    orderDeliveryMethods: [],
    productTypes: [],
    products: [],
    startAtUtc0: 0,
    endAtUtc0: 0,
    voucherRuleId: '',
    type: VoucherTypeEnum.Public,
    name: '',
    label: '',
    code: '',
    description: '',
    discountType: VoucherDiscountTypeEnum.Nominal,
    discountAmount: 0,
    minTotalPrice: 0,
    maxDiscountPrice: 0,
    maxRedemption: 0,
    maxRedemptionPerUser: 0,
    appTypes: [],
    userRoles: [],
    paymentMethods: [],
    isAllStore: true,
    stores: [],
    isAllCategory: true,
    categories: [],
  })
  const [statusFetchVoucher, setStatusFetchVoucher] = useState<'loading' | 'failed' | 'idle'>(
    'idle',
  )
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const [stockLocations, setStockLocations] = useState<
    AdminsEcommerceInventoryStockLocationListResponseItem[]
  >([])
  const [statusFetchStockLocations, setStatusFetchStockLocations] = useState<
    'loading' | 'failed' | 'idle'
  >('loading')
  const [currentPageStockLocations, setCurrentPageStockLocations] = useState(1)
  const [totalPageStockLocations, setTotalPageStockLocations] = useState(1)
  const [limitPageStockLocation] = useState(10)
  const [statusFetchCategories, setStatusFetchCategories] = useState<'loading' | 'failed' | 'idle'>(
    'idle',
  )
  const [categories, setCategories] = useState<AdminsEcommerceGetProductCategoryItem[]>([])
  const maxHeightTable = 500

  useEffect(() => {
    const container = tableContainerRef.current
    if (container) {
      container.addEventListener('scroll', scrollTable)
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', scrollTable)
      }
    }
  }, [statusFetchStockLocations])

  useEffect(() => {
    if (currentPageStockLocations > 1) {
      fetchStockLocation()
    }
  }, [currentPageStockLocations])

  const fetchStockLocation = async () => {
    try {
      setStatusFetchStockLocations('loading')
      const { data } = await adminsApi.adminsEcommerceInventoryStockLocationList(
        '',
        currentPageStockLocations,
        limitPageStockLocation,
      )
      setStatusFetchStockLocations('idle')
      setStockLocations([...stockLocations, ...data.items])
      setTotalPageStockLocations(data.pagination.pages)
    } catch {
      setStatusFetchStockLocations('failed')
    }
  }

  const fetchCategories = async () => {
    try {
      setStatusFetchCategories('loading')
      const { data } = await adminsApi.adminsEcommerceGetProductCategory('', 1, 1000, 'ACTIVE')
      setStatusFetchCategories('idle')
      setCategories(data.items)
    } catch {
      setStatusFetchCategories('failed')
    }
  }

  const createVoucher = async (value: FormVoucherValue) => {
    try {
      setStatusFetchVoucher('loading')
      const { data } = await adminsApi.adminsLoyaltyCreateVoucherRule({
        ...(value.code && value.type === 'PRIVATE' && { code: value.code }),
        minTotalPrice: Number(value.minTotalPrice),
        maxDiscountPrice:
          value.discountType === VoucherDiscountTypeEnum.Percentage ||
          value.discountType === VoucherDiscountTypeEnum.Sawitpoinpercentage
            ? Number(value.maxDiscountPrice)
            : Number(value.discountAmount),
        maxRedemption: Number(value.maxRedemption),
        maxRedemptionPerUser: Number(value.maxRedemptionPerUser),
        orderDeliveryMethods: value.orderDeliveryMethods,
        paymentMethods: value.paymentMethods,
        productTypes: value.productTypes,
        isAllProduct: value.isAllProduct,
        products: value.products,
        isAllStore: value.isAllStore,
        stores: value.isAllStore ? [] : value.stores,
        isAllCategory: value.isAllCategory,
        categories: value.isAllCategory ? [] : value.categories,
        appTypes: value.appTypes,
        userRoles: value.userRoles,
        isSawitPoin: value.isSawitPoin,
      })
      await adminsApi.adminsLoyaltyCreateVoucher({
        voucherRuleId: data.id,
        name: value.name,
        ...(value.code && value.type === 'PRIVATE' && { code: value.code }),
        label: value.label,
        type: value.type,
        description: value.description,
        imageUrl: value.imageUrl,
        discountType: value.discountType,
        discountAmount: Number(value.discountAmount),
        startAtUtc0: value.startAtUtc0,
        endAtUtc0: value.endAtUtc0,
      })
      setStatusFetchVoucher('idle')
      navigate('/loyalty/vouchers', {
        state: {
          message: 'Voucher berhasil dibuat',
        },
      })
    } catch {
      setStatusFetchVoucher('idle')
      await toast('Gagal membuat voucher', 'danger')
    }
  }

  const getVoucherDetail = async (voucherId: string) => {
    try {
      setStatusFetchVoucher('loading')
      const response = await adminsApi.adminsLoyaltyGetVoucherDetail(voucherId)
      setVoucherForm({
        appTypes: response.data.appTypes,
        categories: response.data.categories,
        code: response.data.code,
        description: response.data.description,
        discountAmount: response.data.discountAmount,
        discountType: response.data.discountType,
        endAtUtc0: response.data.endDatetimeAtUtc0,
        imageUrl: response.data.imageUrl,
        isAllCategory: response.data.categories.length === 0,
        isAllProduct: response.data.products.length === 0,
        isAllStore: response.data.stores.length === 0,
        isSawitPoin: response.data.isSawitPoin,
        label: response.data.label,
        maxDiscountPrice: response.data.maxDiscountPrice,
        maxRedemption: response.data.maxRedemption,
        maxRedemptionPerUser: response.data.maxRedemptionPerUser,
        minTotalPrice: response.data.minTotalPrice,
        name: response.data.name,
        orderDeliveryMethods: response.data.orderDeliveryMethods,
        paymentMethods: response.data.paymentMethods,
        productTypes: response.data.productTypes,
        products: response.data.products,
        startAtUtc0: response.data.startDatetimeAtUtc0,
        stores: response.data.stores,
        type: response.data.type as VoucherTypeEnum,
        userRoles: response.data.userRoles,
        voucherRuleId: voucherId,
      })
      setStatusFetchVoucher('idle')
    } catch {
      setStatusFetchVoucher('failed')
    }
  }

  const scrollTable = () => {
    const container = tableContainerRef.current

    if (container) {
      const scrollTop = container.scrollTop
      const contentHeight = container.scrollHeight

      if (
        Math.ceil(scrollTop) + maxHeightTable === contentHeight &&
        currentPageStockLocations < totalPageStockLocations
      ) {
        const nextPage = currentPageStockLocations + 1
        setCurrentPageStockLocations(nextPage)
      }
    }
  }

  const resetFetchStockLocations = () => {
    setStockLocations([])
    setCurrentPageStockLocations(1)
    setTotalPageStockLocations(1)
  }

  return {
    toastContainer,
    statusFetchVoucher,
    statusFetchStockLocations,
    statusFetchCategories,
    voucherForm,
    stockLocations,
    categories,
    tableContainerRef,
    maxHeightTable,
    fetchStockLocation,
    resetFetchStockLocations,
    fetchCategories,
    createVoucher,
    getVoucherDetail,
  }
}
